import React, { useEffect, useState } from 'react';

import { Button } from 'react-bootstrap';
import { css, StyleSheet } from 'aphrodite';
import { faSync } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';

import { backgroundGrey2, blackberry, coolGrey, white } from 'lib/css/colors';
import { GLOBAL_VENDOR_SNOWFLAKE_STATS } from 'lib/networking/endpoints';
import LoadingSpinner from 'components/shared/LoadingSpinner';
import useGet from 'lib/hooks/useGet';

const STAT_CARDS = [
  { key: 'TOTAL_PRODUCT_COUNT', label: 'Total Products', format: 'number' },
  { key: 'TOTAL_ACTIVE_PRODUCT_COUNT', label: 'Active Products', format: 'number' },
  { key: 'TOTAL_BRANDED_PRODUCT_COUNT', label: 'Branded Products', format: 'number' },
  { key: 'TOTAL_PACKER_PRODUCT_COUNT', label: 'Packer Products', format: 'number' },
  { key: 'PRODUCT_COUNT_WITH_KNOWN_SKU', label: 'Products with Known SKU', format: 'number' },
  { key: 'ANNUALIZED_GMV', label: 'Annualized GMV', format: 'currency' },
];

function formatValue(value, format) {
  if (value === null || value === undefined) return '—';
  if (format === 'currency') {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(value);
  }
  return new Intl.NumberFormat('en-US').format(value);
}

function VendorStatsBar({ vendorId }) {
  const [refreshParam, setRefreshParam] = useState(false);

  const {
    data: { stats } = {},
    loading,
    refetch,
  } = useGet(GLOBAL_VENDOR_SNOWFLAKE_STATS, {
    vendor_id: vendorId,
    ...(refreshParam ? { refresh: true } : {}),
  });

  // Reset the refresh flag once the fetch triggered by it has completed.
  useEffect(() => {
    if (!loading && refreshParam) {
      setRefreshParam(false);
    }
  }, [loading, refreshParam]);

  const handleRefresh = () => {
    if (refreshParam) {
      refetch();
    } else {
      setRefreshParam(true);
    }
  };

  if (loading && !stats) {
    return (
      <div className={css(styles.container)}>
        <LoadingSpinner short />
      </div>
    );
  }

  return (
    <div className={css(styles.container)}>
      <div className={css(styles.header)}>
        <span className={css(styles.title)}>Distributor Stats</span>
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={handleRefresh}
          disabled={loading}
          title="Refresh stats from Snowflake"
        >
          <FontAwesomeIcon icon={faSync} className={loading ? css(styles.spinning) : ''} />
          <span className="ml-1">Refresh</span>
        </Button>
      </div>
      <div className={css(styles.cardsRow)}>
        {STAT_CARDS.map(({ key, label, format }) => (
          <div key={key} className={css(styles.card)}>
            <span className={css(styles.cardValue)}>{formatValue(stats?.[key], format)}</span>
            <span className={css(styles.cardLabel)}>{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: white,
    border: `1px solid ${backgroundGrey2}`,
    borderRadius: 8,
    padding: '12px 16px',
    marginBottom: 16,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  title: {
    fontWeight: 600,
    fontSize: 14,
    color: blackberry,
  },
  spinning: {
    animationName: {
      from: { transform: 'rotate(0deg)' },
      to: { transform: 'rotate(360deg)' },
    },
    animationDuration: '1s',
    animationIterationCount: 'infinite',
    animationTimingFunction: 'linear',
  },
  cardsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    backgroundColor: backgroundGrey2,
    borderRadius: 6,
    padding: '8px 16px',
    minWidth: 120,
    flex: '1 1 auto',
  },
  cardValue: {
    fontWeight: 700,
    fontSize: 18,
    color: blackberry,
    lineHeight: 1.2,
  },
  cardLabel: {
    fontSize: 11,
    color: coolGrey,
    marginTop: 2,
    textAlign: 'center',
  },
});

VendorStatsBar.propTypes = {
  vendorId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export default VendorStatsBar;
